'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { DOWNLOAD_ADAPTER_OPTIONS, adapterLabel, type DownloadAdapterId } from '../packages/storefront-download/index';
import { formatMinorMoney } from '../packages/commerce-offer/index';
import { getStoredSession, onAuthStateChange, type AuthSession } from '../lib/supabase/client';
import { useLocale } from '../lib/i18n';
import AuthModal from './AuthModal';
import AssetPreview, { type AssetPreviewMode } from './AssetPreview';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';

type PaymentMethod = 'card' | 'paypal' | 'vietqr';

export type PublishedAsset = {
  listing_id: string;
  title: string;
  summary: string;
  category: string;
  currency: string;
  price_minor: number;
  offer_id: string;
  asset_id: string;
  asset_version: string;
  asset_release_sha256: string;
  slug: string;
  license_id: string | null;
  license_version: string | null;
  distribution_mode: 'free' | 'paid';
  capabilities: string[];
  adapter_ids: string[];
  download_ready_adapter_ids: string[];
  preview_mode: AssetPreviewMode;
  preview_url: string;
  checkout_url: string;
  download_package_url: string;
};

function downloadFilename(response: Response, fallback: string) {
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/i);
  return match?.[1] || fallback;
}

export default function PublishedAssetDetail({ slug }: { slug: string }) {
  const { t, locale } = useLocale();
  const [item, setItem] = useState<PublishedAsset | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [selectedAdapter, setSelectedAdapter] = useState<DownloadAdapterId>('generic');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');

  useEffect(() => {
    setSession(getStoredSession());
    return onAuthStateChange(setSession);
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const controller = new AbortController();
    fetch(`/api/asset-catalog?slug=${encodeURIComponent(slug)}`, { signal: controller.signal })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok || !Array.isArray(payload.items) || !payload.items[0]) throw new Error(payload.error || 'Published asset not found');
        if (cancelled) return;
        const loaded = payload.items[0] as PublishedAsset;
        setItem(loaded);
        const ready = DOWNLOAD_ADAPTER_OPTIONS.find(option => loaded.download_ready_adapter_ids.includes(option.adapter_id));
        if (!ready) throw new Error('No adapter package is currently download-ready.');
        setSelectedAdapter(ready.adapter_id);
      })
      .catch(reason => {
        if (!cancelled && reason?.name !== 'AbortError') setError(reason instanceof Error ? reason.message : 'Published asset unavailable');
      });
    return () => { cancelled = true; controller.abort(); };
  }, [slug]);

  const acquire = async () => {
    if (!item) return;
    if (!session) {
      setAuthOpen(true);
      setError(locale === 'vi' ? 'Đăng nhập để nhận hoặc mua asset này.' : 'Sign in to acquire this asset.');
      return;
    }
    if (!item.download_ready_adapter_ids.includes(selectedAdapter)) {
      setError(`${adapterLabel(selectedAdapter)} package is not download-ready.`);
      return;
    }

    setDownloading(true);
    setError('');
    try {
      const checkout = await fetch(item.checkout_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          listing_id: item.listing_id,
          ...(item.distribution_mode === 'paid' ? { payment_method: paymentMethod } : {}),
        }),
      });
      const checkoutPayload = await checkout.json().catch(() => null);
      if (!checkout.ok) {
        const code = checkoutPayload?.error || 'checkout_unavailable';
        if (code === 'paypal_payment_provider_not_configured') throw new Error(locale === 'vi' ? 'PayPal chưa được cấu hình trên máy chủ.' : 'PayPal is not configured on the server.');
        if (code === 'vietqr_payment_provider_not_configured') throw new Error(locale === 'vi' ? 'VietQR chưa được cấu hình xác minh thanh toán.' : 'VietQR payment verification is not configured.');
        if (code === 'card_payment_provider_not_configured') throw new Error(locale === 'vi' ? 'Thanh toán thẻ chưa được cấu hình trên máy chủ.' : 'Card checkout is not configured on the server.');
        throw new Error(code);
      }

      if (item.distribution_mode === 'paid') {
        const redirectUrl = String(checkoutPayload?.redirect_url || '');
        if (!redirectUrl.startsWith('https://')) throw new Error('payment_redirect_unavailable');
        window.location.assign(redirectUrl);
        return;
      }

      if (!checkoutPayload?.entitlement_token) throw new Error('Download entitlement unavailable');
      const packageResponse = await fetch(item.download_package_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${checkoutPayload.entitlement_token}` },
        body: JSON.stringify({
          product_id: item.asset_id,
          product_version: item.asset_version,
          adapter_id: selectedAdapter,
          asset_release_sha256: item.asset_release_sha256,
        }),
      });
      if (!packageResponse.ok) {
        const payload = await packageResponse.json().catch(() => null);
        throw new Error(payload?.error || 'Adapter package unavailable');
      }

      const blob = await packageResponse.blob();
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = downloadFilename(packageResponse, `${item.slug}-${selectedAdapter}.zip`);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Checkout unavailable');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-16 min-h-[calc(100vh-80px)]">
      <div>
        <div className="mb-6">
          <Button asChild variant="link" className="p-0 h-auto gap-1.5 text-primary text-sm font-semibold">
            <Link href="/#catalog">
              <ArrowLeft className="h-4 w-4" />
              <span>{t('detail.back_to_catalog')}</span>
            </Link>
          </Button>
        </div>
        {error ? <p className="preview-error text-destructive mb-5" role="alert">{error}</p> : null}
        {!item && !error ? <p className="catalog-caption">Loading production asset details…</p> : null}
        {item ? (
          <article className="sprite-detail grid grid-cols-1 md:grid-cols-[minmax(280px,420px)_1fr] gap-8 items-start">
            <div
              className="min-h-[360px] flex items-center justify-center rounded-md border border-border bg-[#151a20] p-4 overflow-hidden"
              style={{
                backgroundImage: 'conic-gradient(#20262e 25%, transparent 0 50%, #20262e 0 75%, transparent 0)',
                backgroundSize: '20px 20px',
              }}
            >
              <AssetPreview src={item.preview_url} alt={item.title} mode={item.preview_mode} style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain', imageRendering: 'pixelated' }} />
            </div>
            <div className="sprite-detail-content flex flex-col gap-4">
              <div>
                <Badge variant="tag" className="mb-2">{item.category}</Badge>
                <h1 className="text-2xl md:text-4xl font-bold font-serif text-foreground">{item.title}</h1>
              </div>
              <p className="text-muted-foreground text-sm md:text-base max-w-xl leading-relaxed">{item.summary}</p>
              
              <div className="flex flex-col gap-2 max-w-sm">
                <label htmlFor="download-adapter" className="text-xs font-semibold text-muted-foreground">{t('detail.download_format')}</label>
                <Select value={selectedAdapter} onValueChange={(val) => setSelectedAdapter(val as DownloadAdapterId)}>
                  <SelectTrigger id="download-adapter" className="h-10 bg-card border-border text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOWNLOAD_ADAPTER_OPTIONS.map(option => {
                      const declared = item.adapter_ids.includes(option.adapter_id);
                      const ready = item.download_ready_adapter_ids.includes(option.adapter_id);
                      const suffix = !declared ? ' — not offered' : !ready ? ' — compiler pending' : '';
                      return (
                        <SelectItem key={option.adapter_id} value={option.adapter_id} disabled={!declared || !ready}>
                          {option.label}{suffix}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground m-0">{t('detail.one_package_per_download')}</p>
              </div>

              {item.distribution_mode === 'paid' ? (
                <div className="flex flex-col gap-2 max-w-sm">
                  <label className="text-xs font-semibold text-muted-foreground">{locale === 'vi' ? 'Phương thức thanh toán' : 'Payment method'}</label>
                  <Select value={paymentMethod} onValueChange={(val) => setPaymentMethod(val as PaymentMethod)}>
                    <SelectTrigger className="h-10 bg-card border-border text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">{locale === 'vi' ? 'Thẻ — Stripe Checkout' : 'Card — Stripe Checkout'}</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="vietqr">VietQR</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground m-0">{locale === 'vi' ? 'Quyền sở hữu chỉ được cấp sau khi máy chủ xác minh thanh toán.' : 'Ownership is granted only after server-side payment verification.'}</p>
                </div>
              ) : null}

              <div className="pt-2">
                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  onClick={acquire}
                  disabled={downloading || !item.download_ready_adapter_ids.includes(selectedAdapter)}
                  className="font-bold text-sm h-11 px-6 shadow-md"
                >
                  {downloading ? t('detail.preparing_download') : item.distribution_mode === 'free' ? `${t('catalog.download')} ${adapterLabel(selectedAdapter)} Pack` : `${t('catalog.buy')} · ${formatMinorMoney(item.price_minor, item.currency)}`}
                </Button>
              </div>

              <section className="border-t border-border pt-4 mt-4 grid gap-2 text-xs text-muted-foreground">
                <p><strong className="text-foreground">{t('detail.distribution')}</strong> {item.distribution_mode === 'free' ? t('detail.free_commercial') : t('detail.paid_commercial')}</p>
                <p><strong className="text-foreground">{t('detail.license')}</strong> {item.license_id || 'Picel Studio License'}</p>
                <p><strong className="text-foreground">Library:</strong> Persistent ownership; legitimate re-downloads are not capped.</p>
              </section>
            </div>
          </article>
        ) : null}
      </div>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} onSuccess={setSession} />
    </main>
  );
}
